import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useRef, useState } from 'react'
import type { QueryKey } from '@tanstack/react-query'

export type SseQueryDataStatus = 'loading' | 'fresh' | 'stale'

/** Re-read owned views without replaying events or owning the SSE transport. */
export function useSseQueryRecovery(
    queryKeys: ReadonlyArray<QueryKey>,
    pollInterval?: number,
) {
    const queryClient = useQueryClient()
    const mounted = useRef(false)
    const [dataStatus, setDataStatus] = useState<SseQueryDataStatus>('loading')
    const updateStatus = useCallback(() => {
        if (!mounted.current) return
        const queries = queryKeys.flatMap((queryKey) =>
            queryClient.getQueryCache().findAll({ queryKey, type: 'active' }),
        )
        if (queries.some(({ state }) => state.status === 'error')) {
            setDataStatus('stale')
        } else if (
            queries.length === 0 ||
            queries.some(
                ({ state }) =>
                    state.status === 'pending' || state.fetchStatus !== 'idle',
            )
        ) {
            setDataStatus('loading')
        } else {
            setDataStatus('fresh')
        }
    }, [queryClient, queryKeys])

    const refetch = useCallback(async () => {
        if (document.visibilityState === 'hidden') return
        await Promise.all(
            queryKeys.map((queryKey) =>
                queryClient.refetchQueries(
                    { queryKey, type: 'active' },
                    { cancelRefetch: false },
                ),
            ),
        )
        updateStatus()
    }, [queryClient, queryKeys, updateStatus])

    useEffect(() => {
        mounted.current = true
        const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
            if (event.type === 'updated') updateStatus()
        })
        const recover = () => {
            void refetch()
        }
        // Wait for sibling query observers to mount, including cached queries.
        queueMicrotask(() => {
            if (mounted.current) recover()
        })
        window.addEventListener('focus', recover)
        document.addEventListener('visibilitychange', recover)
        const timer = pollInterval
            ? setInterval(recover, pollInterval)
            : undefined
        return () => {
            mounted.current = false
            unsubscribe()
            window.removeEventListener('focus', recover)
            document.removeEventListener('visibilitychange', recover)
            clearInterval(timer)
        }
    }, [pollInterval, queryClient, refetch, updateStatus])

    return { dataStatus, refetch }
}
